import { useQuery } from '@apollo/client';
import { ResellerTaxExemptType } from '@/types/gql-op-types';
import NoticeIcon from '../../components/NoticeIcon/NoticeIcon';
import { useToast } from '../../components/Toast/ToastContext';
import { letterName } from '../../lib/documentLabels';
import { useRescueContext } from '../../lib/RescueContext';
import {
  rescueDocumentOverviewQuery,
  type RescueDocumentOverviewData,
  type RescueExemptionNotice,
} from '../../lib/documentQueries';
import AccountSectionCard from './AccountSectionCard';
import DocumentRow from './DocumentRow';
import { useExemptionDocumentUpload } from './useExemptionDocumentUpload';
import styles from './Account.module.scss';

/**
 * Why the portal can't state a requirement, in the portal's own words.
 *
 * The server sends a kind and a remedy rather than copy, so wording changes here without a backend
 * deploy. The remedy decides where they are sent: a rescue with no reseller record has no address
 * card to edit, so telling them to go and fix it would send them to a screen that can't.
 */
function noticeText(notice: RescueExemptionNotice): string {
  const selfServe = notice.remedy === 'SELF_SERVE_ACCOUNT_PAGE';
  switch (notice.kind) {
    case 'ADDRESS_REQUIRED':
      return selfServe
        ? 'Add your primary shipping address above and we can tell you which tax document your state asks for.'
        : 'We need your shipping address before we can tell you which tax document your state asks for. Get in touch and we will add it.';
    case 'JURISDICTION_UNSUPPORTED':
      return 'We cannot yet work out the tax position for your jurisdiction. Get in touch and the Fi team will sort it out.';
    case 'MAY_BE_GOVERNMENT_ENTITY':
      return selfServe
        ? 'If your shelter is run by a city, county or state, set your organization type above. Government shelters file a different document.'
        : 'If your shelter is run by a city, county or state, get in touch. Government shelters file a different document.';
  }
}

/**
 * Everything this rescue is asked to file: the federal letter, plus a certificate for each
 * jurisdiction it ships to.
 *
 * The certificate for the primary shipping address appears before any order exists, so a rescue can
 * get it into review ahead of needing it. Certificates for other states accrue through the order
 * flow, which is the only place a different ship-to is named.
 */
export default function CardExemptionDocuments() {
  const { showToast } = useToast();
  const { taxExemptType } = useRescueContext();
  const government = taxExemptType === ResellerTaxExemptType.GOVERNMENT_ENTITY;
  const { data, loading } = useQuery<RescueDocumentOverviewData>(rescueDocumentOverviewQuery);
  const { fileInputRef, handleFileSelected, pickFileFor, fileError, uploadingFor, keyFor } = useExemptionDocumentUpload(
    () => showToast('Uploaded — pending review by the Fi team'),
  );

  const overview = data?.rescueDocumentOverview;
  const documents = overview?.documents ?? [];
  const notices = overview?.notices ?? [];

  const letters = documents.filter((required) => required.documentType === 'NONPROFIT_LETTER');
  // Sorted by state name, and absent entirely for a rescue that is not asked for one: a rescue
  // outside Fi Nano, or one shipping to a state that collects nothing, owes no certificate.
  const certificates = documents
    .filter((required) => required.documentType === 'STATE_EXEMPTION')
    .slice()
    .sort((a, b) => (a.state ?? '').localeCompare(b.state ?? ''));

  const renderRow = (required: (typeof documents)[number]) => (
    <DocumentRow
      key={keyFor(required.documentType, required.state)}
      required={required}
      uploading={uploadingFor === keyFor(required.documentType, required.state)}
      onPickFile={() => pickFileFor(required.documentType, required.state)}
      government={government}
    />
  );

  return (
    <AccountSectionCard
      title="Nonprofit Documents"
      description="Fi offers free Fi Minis and Fi Nanos to registered nonprofits. The Fi team reviews each document, usually within a few business days."
    >
      <input
        ref={fileInputRef}
        type="file"
        accept="application/pdf,.pdf"
        onChange={handleFileSelected}
        style={{ display: 'none' }}
      />

      {loading && documents.length === 0 ? (
        <div className={styles.loading}>Loading…</div>
      ) : (
        <>
          {notices.map((notice) => (
            <div key={notice.kind} className={styles.documentNotice}>
              <NoticeIcon variant="info" />
              <div>{noticeText(notice)}</div>
            </div>
          ))}

          {letters.length > 0 && (
            <div className={styles.documentGroup}>
              <div className={styles.documentGroupHead}>
                <h4 className={styles.documentGroupTitle}>{letterName(government)}</h4>
                <span className={styles.documentGroupNote}>Fi Minis and Fi Nanos</span>
              </div>
              {letters.map(renderRow)}
            </div>
          )}

          {certificates.length > 0 && (
            <div className={styles.documentGroup}>
              <div className={styles.documentGroupHead}>
                <h4 className={styles.documentGroupTitle}>State sales tax exemption certificates</h4>
                <span className={styles.documentGroupNote}>Fi Nanos · one per ship-to state</span>
              </div>
              {certificates.map(renderRow)}
            </div>
          )}

          {fileError && <div className={styles.uploadError}>{fileError}</div>}
        </>
      )}
    </AccountSectionCard>
  );
}
